// AdminPreorderSummary: Aggregates preorder demand by season, store item, and plant
import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	Select,
	MenuItem,
	CircularProgress,
	Card,
	CardContent,
	Chip,
	Grid,
} from "@mui/material";

import supabase from "../../../supabaseClient";

export default function AdminPreorderSummary() {
	const [loading, setLoading] = useState(true);
	const [orders, setOrders] = useState([]);
	const [seasons, setSeasons] = useState([]);
	const [seasonId, setSeasonId] = useState("");

	useEffect(() => {
		supabase
			.from("seasons")
			.select("id, name")
			.then(({ data }) => setSeasons(data || []));
	}, []);

	useEffect(() => {
		setLoading(true);
		// Query paid orders and aggregate preorder demand
		const fetchOrders = async () => {
			let query = supabase.from("orders").select(
				`
  id,
  status,
  total_cents,
  created_at,
  user_id,
  order_view_token,
  order_items(
    quantity,
    store_item:store_items(
      id,
      title,
      price_cents,
      season_id
    )
  )
`,
			);
			if (seasonId) {
				query = query.eq("order_items.store_items.season_id", seasonId);
			}
			const { data, error } = await query;
			if (error || !data) {
				setOrders([]);
				setLoading(false);
				return;
			}
			setOrders(data);
			setLoading(false);
		};
		fetchOrders();
	}, [seasonId]);

	return (
		<Box sx={{ mx: "auto", width: "100%" }}>
			<Typography variant="h5" gutterBottom>
				Preorder Orders
			</Typography>
			<Box mb={2}>
				<Select
					value={seasonId}
					onChange={(e) => setSeasonId(e.target.value)}
					displayEmpty
					size="small"
					sx={{ minWidth: 180 }}
				>
					<MenuItem value="">All Seasons</MenuItem>
					{seasons.map((s) => (
						<MenuItem key={s.id} value={s.id}>
							{s.name}
						</MenuItem>
					))}
				</Select>
			</Box>
			{loading ? (
				<CircularProgress />
			) : orders.length === 0 ? (
				<Typography>No data.</Typography>
			) : (
				<Grid container spacing={2}>
					{orders.map((order) => (
						<Grid item xs={12} sm={6} md={4} key={order.id}>
							<Card variant="outlined" sx={{ height: "100%" }}>
								<CardContent>
									<Typography variant="subtitle2" color="text.secondary">
										Order ID
									</Typography>
									<Typography variant="body2" sx={{ mb: 1 }}>
										{order.id}
									</Typography>
									<Chip
										label={order.status.toUpperCase()}
										color={order.status === "paid" ? "success" : "warning"}
										size="small"
										sx={{ mb: 1 }}
									/>
									<Typography variant="subtitle2">Total:</Typography>
									<Typography variant="body2" sx={{ mb: 1 }}>
										${(order.total_cents / 100).toFixed(2)}
									</Typography>
									<Typography variant="subtitle2">Placed:</Typography>
									<Typography variant="body2" sx={{ mb: 1 }}>
										{new Date(order.created_at).toLocaleString()}
									</Typography>
									{order.order_items && order.order_items.length > 0 && (
										<Box sx={{ mt: 2 }}>
											<Typography variant="subtitle2">Items:</Typography>
											<ul style={{ margin: 0, paddingLeft: 18 }}>
												{order.order_items.map((item, idx) => (
													<li key={idx}>
														{item.quantity}x{" "}
														{item.store_item?.title || "Unknown Item"}{" "}
														{item.store_item?.price_cents
															? `($${(
																	item.store_item.price_cents / 100
																).toFixed(2)})`
															: ""}
													</li>
												))}
											</ul>
										</Box>
									)}
								</CardContent>
							</Card>
						</Grid>
					))}
				</Grid>
			)}
		</Box>
	);
}
